import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TrajectoryChart - Stacked bar chart for vendor spend trajectory
 *
 * Shows fiscal year/quarter spend broken down by agency, with optional line overlay.
 * Presentational component - data via props, theme via CSS variables.
 *
 * Features:
 * - Stacked bars with top agencies + "Other" bucket
 * - Year/Quarter granularity toggle (optional)
 * - Smart flipping tooltip that never covers hovered bar
 * - Agency line chart for "agencies with spend" or single-agency spend
 * - Click-to-filter legend
 * - Segment drilldown with date range
 * - YoY/QoQ change indicator
 */
import { useState, useMemo, useRef, useCallback } from 'react';
import { getFiscalCalendar, fiscalYearRange, fiscalQuarterRange, } from '@techstack/core';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Cell, } from 'recharts';
import { chartPalette, chartOtherColor } from '../tokens.js';
import { ChartDrillList } from './ChartDrillList.js';
/* Agencies take their colour from the shared ramp — the same slots a partner
   or a vendor takes, so one entity is one colour wherever it is charted. */
const AGENCY_PALETTE = chartPalette;
const OTHER_COLOR = chartOtherColor; // a residual, never a series
/** Format currency for display */
const formatCurrency = (value) => {
    if (value >= 1000000000)
        return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000)
        return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000)
        return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};
/**
 * Date range for a fiscal year on the ACTIVE calendar. Both of these used to
 * hardcode Jul 1 – Jun 30; the boundary now comes off the registry row, so a
 * drilldown always pulls the transactions the bar was actually made of.
 */
function fyDateRange(year, cal) {
    return fiscalYearRange(year, cal);
}
/** Date range for a fiscal quarter on the active calendar (PANW Q1 = Aug–Oct). */
function fyQuarterDateRange(year, quarter, cal) {
    return fiscalQuarterRange(year, quarter, cal);
}
/** Get date range for calendar year */
function calendarYearDateRange(year) {
    return {
        start: `${year}-01-01`,
        end: `${year}-12-31`,
    };
}
/** Custom tooltip - positioned near hovered bar */
const CustomTooltip = ({ rows, title, total, hoveredAgency, onDrill }) => {
    if (!rows || rows.length === 0)
        return null;
    return (_jsxs("div", { className: "cht", children: [_jsx("div", { className: "cht__title", children: title }), _jsx("div", { className: "cht__rows", children: rows.map((row) => (_jsxs("div", { className: `cht__row${hoveredAgency === row.agency ? ' is-hovered' : ''}`, style: { '--swatch': row.color }, children: [_jsx("span", { className: "cht__swatch" }), _jsx("span", { className: "cht__name", children: row.agency }), _jsx("span", { className: "cht__val", children: formatCurrency(row.value) })] }, row.agency))) }), _jsxs("div", { className: "cht__total", children: [_jsx("span", { className: "cht__total-label", children: "Total" }), _jsx("span", { className: "cht__total-val", children: formatCurrency(total) })] }), onDrill && (_jsxs("button", { type: "button", onClick: onDrill, className: "cht__drill", children: ["View transactions", _jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", children: [_jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" }), _jsx("polyline", { points: "12 5 19 12 12 19" })] })] }))] }));
};
/** Custom legend with click-to-filter */
const CustomLegend = ({ agencies, colorMap, selectedAgency, hoveredAgency, onAgencyClick, onAgencyHover }) => {
    return (_jsx("div", { className: "chl chl--pills", onMouseLeave: () => onAgencyHover?.(null), children: agencies.map((agency) => {
            const isSelected = selectedAgency === agency;
            const isDimmed = !!hoveredAgency && hoveredAgency !== agency;
            return (_jsxs("button", { type: "button", onClick: () => onAgencyClick?.(agency), onMouseEnter: () => onAgencyHover?.(agency), className: `chl__item${isSelected ? ' is-selected' : ''}${isDimmed ? ' is-dimmed' : ''}`, style: { '--swatch': colorMap[agency] || OTHER_COLOR }, children: [_jsx("span", { className: "chl__swatch" }), agency] }, agency));
        }) }));
};
/** Granularity toggle component */
const GranularityToggle = ({ value, onChange, quarterDisabled }) => {
    return (_jsxs("div", { className: "chg", children: [_jsxs("button", { type: "button", onClick: () => onChange('year'), className: `chg__btn${value === 'year' ? ' is-active' : ''}`, title: "View by fiscal year", children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }), "Year"] }), _jsxs("button", { type: "button", onClick: () => !quarterDisabled && onChange('quarter'), className: `chg__btn${value === 'quarter' ? ' is-active' : ''}`, title: quarterDisabled ? 'Quarterly data not available' : 'View by fiscal quarter', disabled: quarterDisabled, children: [_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" }), _jsx("line", { x1: "12", y1: "10", x2: "12", y2: "22" })] }), "Quarter"] })] }));
};
export function TrajectoryChart({ data, topAgencies = [], colorMap: colorMapProp, agencyDeptIds = {}, height = 360, showAgencyLine = false, yoyChange, onAgencyClick, selectedAgency, granularity = 'year', onGranularityChange, showGranularityToggle = false, quarterDisabled = false, onSegmentClick, isSfy = true, fiscalCalendar, title, subtitle, className, maxBarSize = 64, barCategoryGap = '20%', }) {
    const [hoveredAgency, setHoveredAgency] = useState(null);
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [tooltipPos, setTooltipPos] = useState(null);
    const chartContainerRef = useRef(null);
    // Touch devices get tap-to-preview → tap-again / CTA-to-drill.
    // With a mouse, hover previews and a plain click drills (unchanged).
    const isCoarsePointer = useMemo(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(hover: none), (pointer: coarse)').matches
        : false, []);
    // Set on touchstart when the tapped segment was NOT already the
    // active one: that tap is a preview, so the click it synthesizes
    // must not drill. iOS often withholds that click anyway (hover
    // handlers mutate the DOM), but this makes the behavior identical
    // whether or not Safari's heuristic kicks in.
    const previewTapRef = useRef(false);
    // Build color map for agencies
    const colorMap = useMemo(() => {
        if (colorMapProp)
            return colorMapProp;
        const map = {};
        topAgencies.forEach((agency, idx) => {
            map[agency] = AGENCY_PALETTE[idx % AGENCY_PALETTE.length];
        });
        map['Other'] = OTHER_COLOR;
        return map;
    }, [topAgencies, colorMapProp]);
    // Transform data for stacked bar chart
    const chartData = useMemo(() => {
        return data.map((point) => {
            const result = {
                period: point.period,
                year: point.year,
                quarter: point.quarter || 0,
                totalSpend: point.totalSpend,
                agencyCount: point.agencyCount || 0,
            };
            if (point.agencies) {
                let otherSum = 0;
                Object.entries(point.agencies).forEach(([agency, spend]) => {
                    if (topAgencies.includes(agency)) {
                        result[agency] = spend;
                    }
                    else {
                        otherSum += spend;
                    }
                });
                if (otherSum > 0) {
                    result['Other'] = otherSum;
                }
            }
            return result;
        });
    }, [data, topAgencies]);
    // Agency keys for stacking (in order)
    const stackKeys = useMemo(() => {
        const keys = [...topAgencies];
        if (chartData.some((d) => d['Other'] > 0)) {
            keys.push('Other');
        }
        return keys;
    }, [topAgencies, chartData]);
    // Compute tooltip position (smart flipping)
    const computeTooltipPos = useCallback((periodIdx, totalPeriods) => {
        const el = chartContainerRef.current;
        if (!el)
            return null;
        const rect = el.getBoundingClientRect();
        const yAxisW = 65;
        const rightPad = 10;
        const tooltipW = 230;
        const gap = 14;
        const plotW = Math.max(50, rect.width - yAxisW - rightPad);
        const barCenterX = yAxisW + ((periodIdx + 0.5) / Math.max(1, totalPeriods)) * plotW;
        const rightEdge = barCenterX + gap + tooltipW;
        const overflowsRight = rightEdge > rect.width - 6;
        const left = overflowsRight
            ? Math.max(8, barCenterX - gap - tooltipW)
            : barCenterX + gap;
        return { left, top: 14 };
    }, []);
    // Handle segment click for drilldown
    const handleSegmentClick = useCallback((agency, dataPoint) => {
        if (!onSegmentClick || agency === 'Other')
            return;
        const year = Number(dataPoint.year);
        const quarter = dataPoint.quarter ? Number(dataPoint.quarter) : undefined;
        const period = String(dataPoint.period);
        // Absent prop → the state calendar, i.e. exactly the old behaviour.
        const cal = fiscalCalendar ?? getFiscalCalendar();
        let dateRange;
        if (quarter && granularity === 'quarter') {
            dateRange = isSfy ? fyQuarterDateRange(year, quarter, cal) : {
                start: `${year}-${String((quarter - 1) * 3 + 1).padStart(2, '0')}-01`,
                end: `${year}-${String(quarter * 3).padStart(2, '0')}-${quarter === 1 || quarter === 4 ? '31' : quarter === 2 ? '30' : '31'}`,
            };
        }
        else {
            dateRange = isSfy ? fyDateRange(year, cal) : calendarYearDateRange(year);
        }
        onSegmentClick({
            agency,
            period,
            year,
            quarter,
            startDate: dateRange.start,
            endDate: dateRange.end,
            departmentId: agencyDeptIds[agency],
            spend: Number(dataPoint[agency]) || 0,
        });
    }, [onSegmentClick, granularity, isSfy, fiscalCalendar, agencyDeptIds]);
    /*
     * One entry per DRAWN segment, period by period, in stacking order.
     * "Other" is excluded exactly as `handleSegmentClick` excludes it — it is
     * a catch-all bucket with no department behind it, so offering a control
     * that returns early would be a tab stop that does nothing.
     */
    const drillTargets = useMemo(() => {
        if (!onSegmentClick)
            return [];
        const out = [];
        for (const point of chartData) {
            for (const agency of stackKeys) {
                if (agency === 'Other')
                    continue;
                const spend = Number(point[agency]) || 0;
                if (!spend)
                    continue;
                out.push({
                    key: `${point.period}-${agency}`,
                    label: `${agency}, ${point.period}, ${formatCurrency(spend)} — open transactions`,
                    onSelect: () => handleSegmentClick(agency, point),
                });
            }
        }
        return out;
    }, [chartData, stackKeys, onSegmentClick, handleSegmentClick]);
    // Determine opacity based on selection/hover
    const getOpacity = (agency, period) => {
        // Legend hover takes precedence
        if (hoveredAgency) {
            return hoveredAgency === agency ? 1 : 0.15;
        }
        // Segment hover
        if (hoveredSegment) {
            return hoveredSegment.period === period && hoveredSegment.agency === agency ? 1 : 0.35;
        }
        // Selection
        if (selectedAgency) {
            return selectedAgency === agency ? 1 : 0.2;
        }
        return 1;
    };
    if (!data || data.length === 0) {
        return (_jsx("div", { className: `chs__empty${className ? ` ${className}` : ''}`, style: { '--h': `${height}px` }, children: "No trajectory data available" }));
    }
    // Prepare tooltip data
    const hoveredData = hoveredSegment
        ? chartData.find((d) => d.period === hoveredSegment.period)
        : null;
    const tooltipRows = hoveredData
        ? stackKeys
            .map((agency) => ({
            agency,
            value: Number(hoveredData[agency]) || 0,
            color: colorMap[agency] || OTHER_COLOR,
        }))
            .filter((r) => r.value > 0)
            .sort((a, b) => b.value - a.value)
        : [];
    const tooltipTotal = tooltipRows.reduce((sum, r) => sum + r.value, 0);
    return (_jsxs("div", { className: `chs${className ? ` ${className}` : ''}`, children: [(title || showGranularityToggle || selectedAgency) && (_jsxs("div", { className: "chs__head", children: [title && (_jsxs("h2", { className: "chs__title", children: [title, selectedAgency && _jsxs("span", { className: "chs__title-sub", children: ["\u2014 ", selectedAgency] })] })), _jsxs("div", { className: "chs__controls", children: [showGranularityToggle && onGranularityChange && (_jsx(GranularityToggle, { value: granularity, onChange: onGranularityChange, quarterDisabled: quarterDisabled })), selectedAgency && onAgencyClick && (_jsxs("button", { type: "button", onClick: () => onAgencyClick(selectedAgency), className: "chs__clear", children: [_jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }), "Clear filter"] }))] })] })), (subtitle || yoyChange) && (_jsxs("p", { className: "chs__subtitle", children: [subtitle, yoyChange && (_jsxs("span", { className: `chs__delta chs__delta--${yoyChange.direction}`, children: [yoyChange.direction === 'up' && '▲', yoyChange.direction === 'down' && '▼', yoyChange.direction === 'flat' && '▬', ' ', Math.abs(yoyChange.percent).toFixed(0), "%", granularity === 'quarter' ? ' QoQ' : ' YoY', ' ', "(", yoyChange.currentFormatted, " in ", yoyChange.currentLabel, " vs ", yoyChange.previousFormatted, " in ", yoyChange.previousLabel, ")"] }))] })), _jsxs("div", { ref: chartContainerRef, className: "chs__plot", style: { '--h': `${height}px` }, children: [_jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(ComposedChart, { data: chartData, margin: { top: 8, right: showAgencyLine ? 50 : 8, left: 0, bottom: granularity === 'quarter' ? 50 : 0 }, barCategoryGap: barCategoryGap, onMouseLeave: () => {
                                setHoveredSegment(null);
                                setTooltipPos(null);
                            }, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "var(--color-border, #3f3f46)", vertical: false }), _jsx(XAxis, { dataKey: "period", axisLine: false, tickLine: false, tick: {
                                        fill: 'var(--color-text-muted, #a1a1aa)',
                                        fontSize: 12,
                                    }, interval: granularity === 'quarter' ? 'preserveStartEnd' : 0, angle: granularity === 'quarter' ? -45 : 0, textAnchor: granularity === 'quarter' ? 'end' : 'middle' }), _jsx(YAxis, { yAxisId: "spend", axisLine: false, tickLine: false, tick: {
                                        fill: 'var(--color-text-muted, #a1a1aa)',
                                        fontSize: 12,
                                    }, tickFormatter: formatCurrency, width: 65 }), showAgencyLine && (_jsx(YAxis, { yAxisId: "count", orientation: "right", axisLine: false, tickLine: false, tick: {
                                        fill: 'var(--color-text-muted, #a1a1aa)',
                                        fontSize: 12,
                                    }, width: 40, allowDecimals: false })), _jsx(Tooltip, { content: () => null, cursor: { fill: 'var(--color-surface-hover, rgba(255,255,255,0.04))' }, wrapperStyle: { display: 'none' } }), stackKeys.map((agency, agencyIdx) => (_jsx(Bar, { yAxisId: "spend", dataKey: agency, stackId: "agencies", fill: colorMap[agency], radius: agency === stackKeys[stackKeys.length - 1] ? [4, 4, 0, 0] : [0, 0, 0, 0], cursor: agency !== 'Other' && onSegmentClick ? 'pointer' : 'default', isAnimationActive: false, maxBarSize: maxBarSize, children: chartData.map((entry, idx) => {
                                        const isHovered = hoveredSegment?.period === entry.period && hoveredSegment?.agency === agency;
                                        const activate = () => {
                                            setHoveredSegment({ period: entry.period, agency });
                                            setTooltipPos(computeTooltipPos(idx, chartData.length));
                                        };
                                        return (_jsx(Cell, { fill: colorMap[agency], fillOpacity: getOpacity(agency, entry.period), stroke: isHovered ? 'var(--color-text, #fff)' : 'transparent', strokeWidth: isHovered ? 1.5 : 0, onMouseEnter: activate, onTouchStart: () => {
                                                previewTapRef.current = !isHovered;
                                                if (!isHovered)
                                                    activate();
                                            }, onClick: () => {
                                                if (previewTapRef.current) {
                                                    // First tap on this segment: preview only.
                                                    previewTapRef.current = false;
                                                    activate();
                                                    return;
                                                }
                                                handleSegmentClick(agency, entry);
                                            } }, `${entry.period}-${agency}`));
                                    }) }, agency))), showAgencyLine && (_jsx(Line, { yAxisId: "count", type: "monotone", dataKey: "agencyCount", stroke: "var(--accent, var(--color-accent, #3b82f6))", strokeWidth: 2, dot: { fill: 'var(--accent, var(--color-accent, #3b82f6))', r: 4 }, activeDot: { r: 6 }, connectNulls: true }))] }) }), hoveredSegment && tooltipPos && tooltipRows.length > 0 && (_jsx("div", { className: "chs__tip", style: { '--x': `${tooltipPos.left}px`, '--y': `${tooltipPos.top}px` }, children: _jsx(CustomTooltip, { rows: tooltipRows, title: hoveredSegment.period, total: tooltipTotal, hoveredAgency: hoveredSegment.agency, onDrill: isCoarsePointer && onSegmentClick && hoveredSegment.agency !== 'Other'
                                ? () => {
                                    const entry = chartData.find((d) => d.period === hoveredSegment.period);
                                    if (entry)
                                        handleSegmentClick(hoveredSegment.agency, entry);
                                }
                                : null }) }))] }), stackKeys.length > 0 && (_jsx(CustomLegend, { agencies: stackKeys, colorMap: colorMap, selectedAgency: selectedAgency, hoveredAgency: hoveredAgency, onAgencyClick: onAgencyClick, onAgencyHover: setHoveredAgency })), _jsx(ChartDrillList, { label: "Spend trajectory \u2014 open transactions by segment", drills: drillTargets })] }));
}
export default TrajectoryChart;
